import { Injectable, signal, computed } from '@angular/core';
import { ExplainModule, UserSession } from '../../core/user/user.model';
import { datadogRum } from '@datadog/browser-rum';
import MEPConfiguration from '../../../assets/mep-configuration/version.json';
import { FEATURE_PREFIX } from '../../../assets/mep-configuration/features-config';
import { firstValueFrom } from 'rxjs';
import { ApiUserService } from './api/user/api-user.service';

@Injectable({ providedIn: 'root' })
export class UserSessionStore {
  private readonly _userSession = signal<UserSession | null>(null);

  readonly userSession = this._userSession.asReadonly();
  readonly isAuthenticated = computed(() => this._userSession() !== null || !!localStorage.getItem('api_token'));
  readonly explainToken = computed(() => this._userSession()?.apiToken || localStorage.getItem('api_token') || '');

  constructor(private apiUserService: ApiUserService) {
    this.restoreFromLocalStorage();
  }

  setSession(session: UserSession): void {
    // TODO: remove this once the API is updated
    session.modules = session.modules.map((module: any) => {
      if (typeof module === 'string')
        return {
          name: module,
        };
      return module;
    });

    localStorage.clear();
    localStorage.setItem('email', session.email);
    localStorage.setItem('user_id', String(session.id));
    localStorage.setItem('group_account_id', String(session.groupAccountId));
    localStorage.setItem('modules', JSON.stringify(session.modules));
    localStorage.setItem('api_token', session.apiToken);
    localStorage.setItem('first_name', session.firstName ?? '');
    localStorage.setItem('last_name', session.lastName ?? '');

    this._userSession.set(session);

    datadogRum.setUser({
      id: String(session.id),
      email: session.email,
      group_account_id: String(session.groupAccountId),
      modules: session.modules,
    });
  }

  clearSession(): void {
    const featureHighlightState = localStorage.getItem(FEATURE_PREFIX + MEPConfiguration.highlight_feature);
    localStorage.clear();
    if (featureHighlightState) {
      localStorage.setItem(FEATURE_PREFIX + MEPConfiguration.highlight_feature, featureHighlightState);
    }
    this._userSession.set(null);
    datadogRum.clearUser();
  }

  restoreFromLocalStorage(): void {
    const modules = localStorage.getItem('modules');
    if (!modules) {
      this.clearSession();
      return;
    }
    const modulesArray = JSON.parse(modules).map((module: any) => {
      if (typeof module === 'string')
        return {
          name: module,
        };
      return module;
    });

    const apiToken = localStorage.getItem('api_token') ?? '';
    if (!apiToken) return;

    this._userSession.set(
      new UserSession({
        id: +(localStorage.getItem('user_id') ?? '-1'),
        group_account_id: +(localStorage.getItem('group_account_id') ?? '-1'),
        first_name: localStorage.getItem('first_name') ?? '',
        last_name: localStorage.getItem('last_name') ?? '',
        email: localStorage.getItem('email') ?? 'unknown-email',
        api_token: apiToken,
        modules: modulesArray,
      })
    );
  }

  updateApiToken(newToken: string): void {
    const session = this._userSession();
    if (!session) return;
    localStorage.removeItem('api_token');
    localStorage.setItem('api_token', newToken);
    this._userSession.set(
      new UserSession({
        id: session.id,
        group_account_id: session.groupAccountId,
        first_name: session.firstName,
        last_name: session.lastName,
        email: session.email,
        api_token: newToken,
        modules: session.modules,
      })
    );
  }
}
